import { ApiError } from '@/lib/api-client';
import { rememberLocationTerminal } from '@/lib/offline-tenders';
import { paymentsService, type TerminalPushStatus } from '@/services/payments.service';

export interface TerminalWaitState {
  terminalName: string;
  amount: number;
  status: TerminalPushStatus;
  transactionReference: string | null;
}

const FAILURE_MSG: Record<Exclude<TerminalPushStatus, 'WAITING_FOR_PAYMENT' | 'SUCCESS'>, string> = {
  DECLINED: 'Payment declined on the terminal — nothing was charged. Try again or choose another method.',
  TIMEOUT: 'The terminal did not confirm in time — nothing was charged. Try again or choose another method.',
  ERROR: 'The terminal reported an error — nothing was charged. Check the device and try again.',
  CANCELLED: 'Terminal payment cancelled — nothing was charged.',
};

const POLL_MS = 1500;

function pause(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
  });
}

/**
 * Push the amount to the location's card/UPI terminal and wait for its verdict.
 *
 * Resolves 'PAID' once the terminal confirms — only then may the caller complete
 * the bill. Resolves 'NO_TERMINAL' when none is registered at the location
 * (production): the cashier takes the payment on a standalone device and the
 * Confirm itself records it. Throws, with a cashier-facing message, when the
 * terminal declines, times out, errors, or the cashier cancels (`signal`).
 */
export async function settleOnTerminal(opts: {
  billId: string;
  amount: number;
  locationId: string;
  signal: AbortSignal;
  onUpdate: (state: TerminalWaitState) => void;
}): Promise<'PAID' | 'NO_TERMINAL'> {
  let push;
  try {
    push = await paymentsService.pushToTerminal({
      bill_id: opts.billId,
      amount: opts.amount,
      location_id: opts.locationId,
    });
  } catch (err) {
    if (err instanceof ApiError && err.statusCode === 404 && /no payment terminal/i.test(err.message)) {
      // T19: remembered so an offline card sale here can use a manual slip.
      rememberLocationTerminal(opts.locationId, false);
      return 'NO_TERMINAL';
    }
    throw err;
  }
  // T19: a real terminal (not the dev mock) answered — card here is terminal-linked.
  rememberLocationTerminal(opts.locationId, !!push.terminal_id);

  const state: TerminalWaitState = {
    terminalName: push.terminal_name,
    amount: opts.amount,
    status: 'WAITING_FOR_PAYMENT',
    transactionReference: null,
  };
  opts.onUpdate(state);

  for (;;) {
    await pause(POLL_MS, opts.signal);
    if (opts.signal.aborted) {
      // Best effort: free the terminal; a push it already settled stays settled.
      await paymentsService.cancelTerminalPush(push.push_id).catch(() => undefined);
      opts.onUpdate({ ...state, status: 'CANCELLED' });
      throw new Error(FAILURE_MSG.CANCELLED);
    }
    const now = await paymentsService.getTerminalPush(push.push_id);
    if (now.status === 'WAITING_FOR_PAYMENT') continue;
    opts.onUpdate({ ...state, status: now.status, transactionReference: now.transaction_reference });
    if (now.status === 'SUCCESS') return 'PAID';
    throw new Error(FAILURE_MSG[now.status]);
  }
}
